import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { ChoiceGroup } from '../../components/Choice';
import { Field, Input, Textarea } from '../../components/Field';
import { useDb } from '../../db/hooks';
import { MODALITY_LABELS } from '../../data/profile';
import { addIntraVitals, createSession, removeVitals, savePhaseVitals, updateSession, type VitalRow } from '../../data/sessions';
import { inputValue } from '../../lib/forms';
import { minutesBetween, type SessionRow } from '../../lib/sessions';
import type { Modality } from '../../lib/schedule';
import { useSessionSymptoms } from '../../data/tracking';
import { useQuickLog } from '../../app/QuickLogSheet';
import { useCanWrite } from '../../account/useCanWrite';
import { afterSchema, beforeSchema, intraSchema, machineSchema } from './fields';
import { BloodPressureFields } from './VitalsFields';

const hasAny = (values: Record<string, unknown>) => Object.values(values).some((value) => value != null);

function FormError({ message }: { message?: string }) {
  return message ? <p className="text-sm text-clinical-danger">{message}</p> : null;
}

/* ---------------------------------------------------------------- Before */

export interface BeforeDefaults {
  session_date: string;
  modality: Modality;
  start_time: string | null;
  dry_weight_kg: number | null;
}

export function BeforeStep({
  session,
  vitals,
  defaults,
  onSaved
}: {
  session: SessionRow | null;
  vitals: VitalRow | undefined;
  defaults: BeforeDefaults;
  onSaved: (sessionId: string) => void;
}) {
  const db = useDb();
  const [more, setMore] = useState(!!(vitals?.temperature_c || vitals?.blood_sugar_mg_dl));
  const form = useForm<z.input<typeof beforeSchema>, unknown, z.output<typeof beforeSchema>>({
    resolver: zodResolver(beforeSchema),
    defaultValues: {
      session_date: session?.session_date ?? defaults.session_date,
      modality: session?.modality ?? defaults.modality,
      start_time: inputValue(session?.start_time ?? defaults.start_time),
      pre_weight_kg: inputValue(session?.pre_weight_kg),
      uf_goal_ml: inputValue(session?.uf_goal_ml),
      bp_systolic: inputValue(vitals?.bp_systolic),
      bp_diastolic: inputValue(vitals?.bp_diastolic),
      pulse: inputValue(vitals?.pulse),
      temperature_c: inputValue(vitals?.temperature_c),
      blood_sugar_mg_dl: inputValue(vitals?.blood_sugar_mg_dl)
    }
  });
  const { errors, isSubmitting } = form.formState;

  async function submit(values: z.output<typeof beforeSchema>) {
    try {
      const { bp_systolic, bp_diastolic, pulse, temperature_c, blood_sugar_mg_dl, ...sessionValues } = values;
      const vitalValues = { bp_systolic, bp_diastolic, pulse, temperature_c, blood_sugar_mg_dl };
      const id = session
        ? (await updateSession(db, session.id, sessionValues)).id
        : (await createSession(db, { ...sessionValues, status: 'in_progress', dry_weight_kg: defaults.dry_weight_kg })).id;
      if (vitals || hasAny(vitalValues)) await savePhaseVitals(db, id, 'pre', vitals, vitalValues);
      onSaved(id);
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : String(error) });
    }
  }

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date" error={errors.session_date?.message}>
          <Input type="date" {...form.register('session_date')} />
        </Field>
        <Field label="Start time" error={errors.start_time?.message}>
          <Input type="time" {...form.register('start_time')} />
        </Field>
      </div>
      <Controller
        control={form.control}
        name="modality"
        render={({ field }) => (
          <ChoiceGroup<Modality>
            name="session-modality"
            value={field.value}
            onChange={field.onChange}
            columns={2}
            options={[
              { value: 'in_center', label: MODALITY_LABELS.in_center },
              { value: 'home_hd', label: MODALITY_LABELS.home_hd }
            ]}
          />
        )}
      />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Weight before (kg)" error={errors.pre_weight_kg?.message}>
          <Input inputMode="decimal" {...form.register('pre_weight_kg')} />
        </Field>
        <Field label="Fluid to remove (mL)" help="The UF goal, if you know it." error={errors.uf_goal_ml?.message}>
          <Input inputMode="numeric" {...form.register('uf_goal_ml')} />
        </Field>
      </div>
      <BloodPressureFields register={form.register} errors={errors} label="Blood pressure before" />
      {more ? (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Temperature (°C)" error={errors.temperature_c?.message}>
            <Input inputMode="decimal" {...form.register('temperature_c')} />
          </Field>
          <Field label="Blood sugar (mg/dL)" error={errors.blood_sugar_mg_dl?.message}>
            <Input inputMode="numeric" {...form.register('blood_sugar_mg_dl')} />
          </Field>
        </div>
      ) : (
        <button type="button" className="text-sm font-medium text-clinical-primary" onClick={() => setMore(true)}>
          + Temperature or blood sugar
        </button>
      )}
      <FormError message={errors.root?.message} />
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Saving…' : session ? 'Save' : 'Start session'}
      </Button>
    </form>
  );
}

/* ---------------------------------------------------------------- During */

function nowTime() {
  return format(new Date(), 'HH:mm');
}

function IntraReadingForm({ session }: { session: SessionRow }) {
  const db = useDb();
  // While a session is running, readings are usually entered as they happen.
  const defaultTime = () => (session.status === 'in_progress' ? nowTime() : '');
  const form = useForm<z.input<typeof intraSchema>, unknown, z.output<typeof intraSchema>>({
    resolver: zodResolver(intraSchema),
    defaultValues: { time: defaultTime(), bp_systolic: '', bp_diastolic: '', pulse: '', notes: '' }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <form
      noValidate
      className="space-y-3 rounded-lg border border-dashed border-clinical-line p-3"
      onSubmit={form.handleSubmit(async ({ time, ...values }) => {
        const recordedAt = parseISO(`${session.session_date}T${time}:00`).toISOString();
        await addIntraVitals(db, session.id, { ...values, recorded_at: recordedAt });
        form.reset({ time: defaultTime(), bp_systolic: '', bp_diastolic: '', pulse: '', notes: '' });
      })}
    >
      <Field label="Time" error={errors.time?.message}>
        <Input type="time" {...form.register('time')} />
      </Field>
      <BloodPressureFields register={form.register} errors={errors} label="Blood pressure during" />
      <Field label="Note (optional)" error={errors.notes?.message}>
        <Input {...form.register('notes')} placeholder="e.g. Cramps, saline given" />
      </Field>
      <Button type="submit" variant="secondary" className="w-full" disabled={isSubmitting}>
        Add reading
      </Button>
    </form>
  );
}

function MachineForm({ session }: { session: SessionRow }) {
  const db = useDb();
  const [saved, setSaved] = useState(false);
  const form = useForm<z.input<typeof machineSchema>, unknown, z.output<typeof machineSchema>>({
    resolver: zodResolver(machineSchema),
    defaultValues: {
      blood_flow_ml_min: inputValue(session.blood_flow_ml_min),
      dialysate_flow_ml_min: inputValue(session.dialysate_flow_ml_min),
      dialyzer: inputValue(session.dialyzer),
      heparin_dose: inputValue(session.heparin_dose),
      needle_gauge: inputValue(session.needle_gauge),
      venous_pressure: inputValue(session.venous_pressure),
      arterial_pressure: inputValue(session.arterial_pressure),
      tmp: inputValue(session.tmp),
      conductivity: inputValue(session.conductivity)
    }
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  const field = (name: keyof z.input<typeof machineSchema>, label: string, mode: 'numeric' | 'decimal' | 'text' = 'numeric') => (
    <Field label={label} error={errors[name]?.message}>
      <Input inputMode={mode} {...form.register(name)} />
    </Field>
  );
  return (
    <form
      noValidate
      className="space-y-3"
      onSubmit={form.handleSubmit(async (values) => {
        await updateSession(db, session.id, values);
        form.reset(form.getValues());
        setSaved(true);
      })}
    >
      <h3 className="text-sm font-semibold">Machine settings</h3>
      <div className="grid grid-cols-2 gap-3">
        {field('blood_flow_ml_min', 'Blood flow (mL/min)')}
        {field('dialysate_flow_ml_min', 'Dialysate flow (mL/min)')}
        {field('venous_pressure', 'Venous pressure (mmHg)')}
        {field('arterial_pressure', 'Arterial pressure (mmHg)', 'text')}
        {field('tmp', 'TMP (mmHg)')}
        {field('conductivity', 'Conductivity (mS/cm)', 'decimal')}
        {field('dialyzer', 'Dialyzer', 'text')}
        {field('needle_gauge', 'Needle gauge', 'text')}
      </div>
      {field('heparin_dose', 'Heparin dose', 'text')}
      <Button type="submit" variant="secondary" className="w-full" disabled={isSubmitting || !isDirty}>
        {saved && !isDirty ? 'Machine settings saved' : 'Save machine settings'}
      </Button>
    </form>
  );
}

function SessionSymptoms({ session }: { session: SessionRow }) {
  const symptoms = useSessionSymptoms(session.id);
  const quickLog = useQuickLog();
  const canWrite = useCanWrite();
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">Symptoms during dialysis</h3>
      {symptoms.length ? (
        <ul className="mb-2 divide-y divide-clinical-line rounded-lg border border-clinical-line">
          {symptoms.map((symptom) => (
            <li key={symptom.id}>
              <button
                type="button"
                disabled={!canWrite}
                onClick={() => quickLog.open({ kind: 'symptom', entry: symptom })}
                className="focus-ring flex w-full items-center gap-3 px-3 py-2 text-left text-sm"
              >
                <span className="w-12 font-medium">{format(new Date(symptom.occurred_at), 'HH:mm')}</span>
                <span className="flex-1">
                  {symptom.symptom} <span className="text-clinical-muted">{symptom.severity}/5</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {canWrite ? (
        <Button type="button" variant="secondary" className="w-full" onClick={() => quickLog.open({ kind: 'symptom', sessionId: session.id })}>
          Add a symptom
        </Button>
      ) : null}
    </div>
  );
}

export function DuringStep({ session, readings, onNext }: { session: SessionRow; readings: VitalRow[]; onNext: () => void }) {
  const db = useDb();
  return (
    <div className="space-y-5">
      <div>
        <h3 className="mb-2 text-sm font-semibold">Readings during dialysis</h3>
        {readings.length ? (
          <ul className="mb-3 divide-y divide-clinical-line rounded-lg border border-clinical-line">
            {readings.map((reading) => (
              <li key={reading.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="w-12 font-medium">{format(new Date(reading.recorded_at), 'HH:mm')}</span>
                <span className="flex-1">
                  {reading.bp_systolic != null ? `${reading.bp_systolic}/${reading.bp_diastolic}` : ''}
                  {reading.pulse != null ? ` · pulse ${reading.pulse}` : ''}
                  {reading.notes ? <span className="block text-xs text-clinical-muted">{reading.notes}</span> : null}
                </span>
                <button
                  type="button"
                  aria-label={`Remove reading at ${format(new Date(reading.recorded_at), 'HH:mm')}`}
                  className="focus-ring rounded p-1 text-clinical-muted hover:text-clinical-danger"
                  onClick={() => void removeVitals(db, reading.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-3 text-sm text-clinical-muted">No readings yet. Add blood pressure checks as they’re taken.</p>
        )}
        <IntraReadingForm session={session} />
      </div>
      <SessionSymptoms session={session} />
      {session.modality === 'home_hd' ? <MachineForm session={session} /> : null}
      <Button className="w-full" onClick={onNext}>
        Next: after dialysis
      </Button>
    </div>
  );
}

/* ---------------------------------------------------------------- After */

export function AfterStep({ session, vitals, onSaved }: { session: SessionRow; vitals: VitalRow | undefined; onSaved: () => void }) {
  const db = useDb();
  const form = useForm<z.input<typeof afterSchema>, unknown, z.output<typeof afterSchema>>({
    resolver: zodResolver(afterSchema),
    defaultValues: {
      end_time: inputValue(session.end_time ?? (session.status === 'in_progress' ? nowTime() : null)),
      post_weight_kg: inputValue(session.post_weight_kg),
      uf_achieved_ml: inputValue(session.uf_achieved_ml),
      bp_systolic: inputValue(vitals?.bp_systolic),
      bp_diastolic: inputValue(vitals?.bp_diastolic),
      pulse: inputValue(vitals?.pulse),
      outcome: session.status === 'ended_early' ? 'ended_early' : 'completed',
      early_end_reason: inputValue(session.early_end_reason),
      notes: inputValue(session.notes)
    }
  });
  const { errors, isSubmitting } = form.formState;
  const outcome = form.watch('outcome');

  async function submit(values: z.output<typeof afterSchema>) {
    try {
      const { bp_systolic, bp_diastolic, pulse, outcome: status, ...rest } = values;
      await updateSession(db, session.id, {
        ...rest,
        status,
        early_end_reason: status === 'ended_early' ? rest.early_end_reason : null,
        duration_min: minutesBetween(session.start_time, rest.end_time)
      });
      const vitalValues = { bp_systolic, bp_diastolic, pulse };
      if (vitals || hasAny(vitalValues)) await savePhaseVitals(db, session.id, 'post', vitals, vitalValues);
      onSaved();
    } catch (error) {
      form.setError('root', { message: error instanceof Error ? error.message : String(error) });
    }
  }

  return (
    <form noValidate onSubmit={form.handleSubmit(submit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="End time" error={errors.end_time?.message}>
          <Input type="time" {...form.register('end_time')} />
        </Field>
        <Field label="Weight after (kg)" error={errors.post_weight_kg?.message}>
          <Input inputMode="decimal" {...form.register('post_weight_kg')} />
        </Field>
      </div>
      <Field label="Fluid removed (mL)" help="The UF volume shown on the machine." error={errors.uf_achieved_ml?.message}>
        <Input inputMode="numeric" {...form.register('uf_achieved_ml')} />
      </Field>
      <BloodPressureFields register={form.register} errors={errors} label="Blood pressure after" />
      <Controller
        control={form.control}
        name="outcome"
        render={({ field }) => (
          <ChoiceGroup<'completed' | 'ended_early'>
            name="outcome"
            value={field.value}
            onChange={field.onChange}
            columns={2}
            options={[
              { value: 'completed', label: 'Completed' },
              { value: 'ended_early', label: 'Ended early' }
            ]}
          />
        )}
      />
      {outcome === 'ended_early' ? (
        <Field label="Why did it end early?" error={errors.early_end_reason?.message}>
          <Input {...form.register('early_end_reason')} placeholder="e.g. Low blood pressure, access problem" />
        </Field>
      ) : null}
      <Field label="Notes (optional)" error={errors.notes?.message}>
        <Textarea {...form.register('notes')} placeholder="How it went, anything to tell your doctor" />
      </Field>
      <FormError message={errors.root?.message} />
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        {isSubmitting ? 'Saving…' : 'Finish session'}
      </Button>
    </form>
  );
}
